<template>
  <div class="code-form base-form">
    <div
      class="code-form__phone"
      data-test="title"
    >
      Мы отправили код на <span>{{ phone }}</span>
    </div>

    <div class="code-form__change-phone black-link">
      <span @click="$emit('changePhone')">Изменить</span>
    </div>

    <label
      for="auth-code"
      class="base-label"
      >Код подтверждения</label
    >

    <div class="code-form__input-wrapper">
      <CodeField
        :code-length="4"
        :is-loading="isLoading"
        :error-message="errorMessage"
        @set-code="setCode"
      />
    </div>

    <button
      class="code-form__confirm-code-btn base-btn base-btn--black"
      :class="{
        'base-btn--disabled': !isAuthAllowed,
      }"
      @click="confirm"
    >
      <Preloader
        v-if="isLoading"
        :is-multiple="true"
      />
      <template v-else-if="isRegistration === undefined"> Отправить </template>
      <template v-else-if="isRegistration"> Зарегистрироваться </template>
      <template v-else> Войти </template>
    </button>

    <!-- Повторная отправка кода подтверждения -->
    <div class="code-form__new-code new-code">
      <div
        v-if="timeLeft > 0"
        class="new-code__timer"
      >
        Получить новый код можно через {{ getFormatTime(timeLeft) }}
      </div>

      <div
        v-else
        class="black-link"
        @click="$emit('sendNewCode')"
      >
        <span>Получить новый код</span>
      </div>
    </div>

    <div class="code-form__support">
      Не приходит СМС? <br />
      Напишите на <a href="mailto:support@motmom.com">support@motmom.com</a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useField } from 'vee-validate';
import { string } from 'yup';

import { Preloader } from '../preloader';
import { CodeField } from '../code-field';

const getFormatTime = (timeLeft: number) => {
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft - minutes * 60;

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const props = defineProps({
  phone: {
    type: String,
    required: true,
  },
  isRegistration: {
    type: Boolean,
    default: undefined,
  },
  allowSendAt: {
    type: String,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
  errorMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['confirm', 'changePhone', 'sendNewCode']);

const timeLeft = ref<number>(0);
const { value: code, meta } = useField<string>(
  'code',
  string().required().matches(/\d{4}/, 'Код подтверждения должен содержать 4 символа'),
  { initialValue: '' },
);

const CODE_VALUE_LENGTH = 4;

const isAuthAllowed = computed(() => {
  return meta.valid && code.value.length === CODE_VALUE_LENGTH;
});

const setCode = (codeValue: string) => {
  code.value = codeValue || '';
};

const confirm = () => {
  if (!meta.valid) {
    return;
  }

  emit('confirm', code.value);
};

const setTimer = () => {
  const currentDate = new Date();
  const allowSendAtDate = new Date(props.allowSendAt);

  currentDate.setTime(currentDate.getTime());

  timeLeft.value = Math.ceil((allowSendAtDate.getTime() - currentDate.getTime()) / 1000);

  const timerId = setInterval(() => {
    timeLeft.value -= 1;

    if (timeLeft.value <= 0) {
      clearInterval(timerId);
    }
  }, 1000);
};

watch(
  () => props.allowSendAt,
  () => {
    setTimer();
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.code-form {
  font-family: 'Montserrat-Regular';

  &__phone {
    font-size: 15px;
    line-height: 19px;
    margin-bottom: 15px;
    text-align: center;

    span {
      font-family: 'Montserrat-Medium';
      white-space: nowrap;
    }
  }

  &__change-phone {
    margin-bottom: 40px;

    span {
      display: inline-block;
      line-height: 14px;
    }
  }

  .new-code {
    margin-top: 24px;

    &__timer {
      color: #6f7071;
      text-align: center;
      font-size: 15px;
    }
  }

  &__confirm-code-btn {
    width: 100%;
  }

  &__support {
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    margin-top: 40px;

    a {
      font-family: 'Montserrat-Medium', sans-serif;
      color: #000;
      text-decoration: none;
    }
  }
}
</style>
